import React from 'react';
import { useTenant } from '../../contexts/TenantContext';
import { useUpdateNodeModules, useUpdateNodeConfig } from '../../hooks/useNode';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Spinner } from '../../components/ui/Spinner';
import { 
  Layers, 
  MessageSquare, 
  Calendar, 
  DollarSign, 
  Target, 
  FileText, 
  Mail, 
  Layout,
  Check,
  AlertTriangle,
  Vote,
  LifeBuoy,
  Lock,
  PieChart,
  BookOpen
} from 'lucide-react';
import { cn } from '../../lib/utils';
import toast from 'react-hot-toast';

const MODULES = [
  { id: 'PULSE', name: 'Pulse', description: 'Announcements and updates', icon: <Layout size={18} />, alwaysOn: true },
  { id: 'CHAT', name: 'Chat', description: 'Real-time messaging channels', icon: <MessageSquare size={18} /> },
  { id: 'CALENDAR', name: 'Calendar', description: 'Event scheduling and RSVPs', icon: <Calendar size={18} /> },
  { id: 'LEDGER', name: 'Ledger', description: 'Financial tracking and payments', icon: <DollarSign size={18} /> },
  { id: 'REQUIREMENTS', name: 'Requirements', description: 'Achievement tracking', icon: <Check size={18} /> },
  { id: 'GOALS', name: 'Goals', description: 'Unified direction and objective tracking', icon: <Target size={18} /> },
  { id: 'NEWSLETTER', name: 'Newsletter', description: 'Email communications', icon: <Mail size={18} /> },
  { id: 'DISCUSSIONS', name: 'Message Board', description: 'Threaded discussions', icon: <FileText size={18} /> },
  { id: 'ELECTIONS', name: 'Elections', description: 'Democratic voting and nominations', icon: <Vote size={18} /> },
  { id: 'SUPPORT', name: 'Help Desk', description: 'Issue reporting and administration support', icon: <LifeBuoy size={18} /> },
  { id: 'VAULT', name: 'Vault', description: 'Secure document storage and verification', icon: <Lock size={18} /> },
  { id: 'POLLS', name: 'Polls', description: 'Quick community sentiment tracking', icon: <PieChart size={18} /> },
  { id: 'DIRECTORY', name: 'Directory', description: 'Publicly searchable member list', icon: <BookOpen size={18} /> },
];

const ModuleChip: React.FC<{
  module: typeof MODULES[0];
  isEnabled: boolean;
  onToggle: () => void;
  isLoading: boolean;
}> = ({ module, isEnabled, onToggle, isLoading }) => (
  <button
    onClick={onToggle}
    disabled={module.alwaysOn || isLoading}
    className={cn(
      "flex flex-col items-start p-4 rounded-none border-2 transition-all text-left group",
      isEnabled 
        ? "bg-navy-900 border-navy-900 text-white" 
        : "bg-white border-black dark:border-white text-gray-700 hover:border-blue-300 dark:bg-slate-900",
      module.alwaysOn && "opacity-80 cursor-default"
    )}
  >
    <div className="flex items-center justify-between w-full mb-3">
      <div className={cn(
        "p-2 rounded-none",
        isEnabled ? "bg-white/10 dark:bg-slate-900" : "bg-white text-gray-500 group-hover:bg-blue-50 group-hover:text-blue-600 dark:bg-slate-800"
      )}>
        {module.icon}
      </div>
      {isLoading ? (
        <Spinner size="sm" className={isEnabled ? "text-white" : "text-blue-600"} />
      ) : isEnabled ? (
        <div className="w-5 h-5 bg-teal-500 rounded-none flex items-center justify-center">
          <Check size={12} strokeWidth={3} />
        </div>
      ) : (
        <div className="w-5 h-5 border-2 border-black dark:border-white rounded-none" />
      )}
    </div>
    <h4 className="font-semibold text-sm mb-1">{module.name}</h4>
    <p className={cn(
      "text-[10px] leading-tight",
      isEnabled ? "text-blue-200" : "text-gray-500"
    )}>
      {module.description}
    </p>
  </button>
);

export const ModuleSettingsPage = () => {
  const { node } = useTenant();
  const updateMutation = useUpdateNodeModules(node?.id || '');
  const updateConfigMutation = useUpdateNodeConfig(node?.id || '');
  const [enabledModules, setEnabledModules] = React.useState<string[]>([]);
  const [includeParentPulse, setIncludeParentPulse] = React.useState<boolean>(
    (node?.config as any)?.includeParentPulse ?? true
  );
  const [publicNewsletterSignup, setPublicNewsletterSignup] = React.useState<boolean>(
    (node?.config as any)?.publicNewsletterSignup ?? true
  );

  const [prevModulesNode, setPrevModulesNode] = React.useState<typeof node | null>(null);
  if (node && prevModulesNode !== node) {
    setPrevModulesNode(node);
    if (node?.modules) {
      if (Array.isArray(node.modules)) {
        setEnabledModules(node.modules);
      } else if (typeof node.modules === 'object') {
        const arr = Object.entries(node.modules).filter(([_, v]) => v).map(([k]) => k);
        setEnabledModules(arr);
      }
    }
    if ((node.config as any)?.includeParentPulse !== undefined) {
      setIncludeParentPulse(Boolean((node.config as any).includeParentPulse));
    }
    if ((node.config as any)?.publicNewsletterSignup !== undefined) {
      setPublicNewsletterSignup(Boolean((node.config as any).publicNewsletterSignup));
    }
  }

  const handleToggleCascade = (key: 'includeParentPulse' | 'publicNewsletterSignup') => {
    const nextVal = key === 'includeParentPulse' ? !includeParentPulse : !publicNewsletterSignup;
    if (key === 'includeParentPulse') setIncludeParentPulse(nextVal);
    else setPublicNewsletterSignup(nextVal);
    updateConfigMutation.mutate({ [key]: nextVal }, {
      onSuccess: () => toast.success('Cascade setting updated'),
      onError: () => toast.error('Failed to update cascade setting')
    });
  };

  const handleToggle = (moduleId: string) => {
    const isCurrentlyEnabled = enabledModules.includes(moduleId);
    
    if (isCurrentlyEnabled) {
      const confirmed = window.confirm(
        `Disabling ${moduleId} will hide all related content. Data is preserved but will not be accessible. Continue?`
      );
      if (!confirmed) return;
    }

    const newModules = isCurrentlyEnabled
      ? enabledModules.filter(id => id !== moduleId)
      : [...enabledModules, moduleId];

    updateMutation.mutate({ [moduleId]: !isCurrentlyEnabled }, {
      onSuccess: () => {
        setEnabledModules(newModules);
        toast.success(`${moduleId} ${isCurrentlyEnabled ? 'disabled' : 'enabled'}`);
      },
      onError: () => toast.error('Failed to update modules')
    });
  };

  return (
    <div className="max-w-7xl mx-auto space-y-6">
      <div className="flex items-center gap-4 mb-8">
        <Button variant="secondary" size="sm" onClick={() => window.history.back()}>
          Back
        </Button>
        <div>
          <h1 className="text-lg md:text-2xl font-semibold text-gray-900 dark:text-white">Module Settings</h1>
          <p className="text-xs md:text-sm text-gray-500">Enable or disable features for your organization.</p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {MODULES.map(module => (
          <ModuleChip 
            key={module.id}
            module={module}
            isEnabled={module.alwaysOn || enabledModules.includes(module.id)}
            onToggle={() => handleToggle(module.id)}
            isLoading={updateMutation.isPending}
          />
        ))}
      </div>

      <Card className="p-6">
        <h3 className="text-sm font-semibold text-gray-900 mb-4 dark:text-white">Cascade & Inheritance</h3>
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <label className="text-sm font-medium text-gray-700">Include Parent Pulse</label>
              <p className="text-xs text-gray-500">Automatically include news from parent organizations in your local newsletters.</p>
            </div>
            <button
              type="button"
              onClick={() => handleToggleCascade('includeParentPulse')}
              className={cn(
                "relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-none border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none",
                includeParentPulse ? "bg-navy-900" : "bg-gray-200"
              )}
            >
              <span className={cn(
                "pointer-events-none inline-block h-5 w-5 transform rounded-none bg-white shadow ring-0 transition duration-200 ease-in-out dark:bg-slate-900",
                includeParentPulse ? "translate-x-5" : "translate-x-0"
              )} />
            </button>
          </div>
          
          <div className="flex items-center justify-between border-t border-black dark:border-white pt-4">
            <div className="space-y-0.5">
              <label className="text-sm font-medium text-gray-700">Public Newsletter Signup</label>
              <p className="text-xs text-gray-500">Allow non-members to subscribe to your organization's public updates.</p>
            </div>
            <button
              type="button"
              onClick={() => handleToggleCascade('publicNewsletterSignup')}
              className={cn(
                "relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-none border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none",
                publicNewsletterSignup ? "bg-navy-900" : "bg-gray-200"
              )}
            >
              <span className={cn(
                "pointer-events-none inline-block h-5 w-5 transform rounded-none bg-white shadow ring-0 transition duration-200 ease-in-out dark:bg-slate-900",
                publicNewsletterSignup ? "translate-x-5" : "translate-x-0"
              )} />
            </button>
          </div>
        </div>
      </Card>

      <Card className="bg-amber-50 border-amber-200 mt-8">
        <div className="flex gap-3">
          <AlertTriangle size={20} className="text-amber-700 flex-shrink-0" />
          <div>
            <p className="text-sm font-semibold text-amber-900">Important Note</p>
            <p className="text-xs text-amber-800 leading-relaxed mt-1">
              Disabling a module hides it from the sidebar and dashboard for all members. All data (messages, transactions, goals) is safely preserved in the database and will reappear if you re-enable the module later.
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
};
